import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Calendar, Sparkles, MessageSquare, Share2, Heart, ChevronDown, ChevronUp, CreditCard, Loader2 } from "lucide-react";
import TrustBadges from "../components/TrustBadges";
import ShareMyExperience from "../components/ShareMyExperience";
import ExpandableBookingList from "../components/ExpandableBookingList";
import EmptyState from "../components/EmptyState";
import FilterPills, { applyBookingFilters, computeBookingFilterCounts } from "../components/FilterPills";
import FavoriteButton from "../components/FavoriteButton";
import CustomOffersPanel from "../components/CustomOffersPanel";
import RecentPlanCard from "../components/RecentPlanCard";
import MyConciergeRequests from "../components/MyConciergeRequests";
import { stageLabel, awaitingPayment } from "../lib/bookingStatus";

export default function CustomerDashboard(){
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [plans, setPlans] = useState([]);
  const [shareBooking, setShareBooking] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [rangeFilter, setRangeFilter] = useState("all");
  const [favorites, setFavorites] = useState([]);
  // "Recent Plans" — show 3 most-recent by default, allow expanding to view
  // the rest without leaving the page. Status itself is purely a UI control;
  // no backend write happens when toggling. (Phase 31 polish.)
  const [plansExpanded, setPlansExpanded] = useState(false);
  // Single-flight payment tracker — booking_id currently being charged via
  // the Pay-Concierge-Fee inline pill. Prevents double-clicks + drives the
  // spinner state on the button.
  const [payingId, setPayingId] = useState(null);
  const [payError, setPayError] = useState("");

  const loadFavorites = useCallback(() => {
    api.get("/favorites").then(r=>setFavorites(Array.isArray(r.data) ? r.data : [])).catch(()=>setFavorites([]));
  }, []);

  const refreshBookings = useCallback(() => {
    api.get("/bookings").then(r=>setBookings(Array.isArray(r.data) ? r.data : [])).catch(()=>setBookings([]));
  }, []);

  const payConciergeFee = useCallback(async (bookingId) => {
    if (!bookingId || payingId) return;
    setPayingId(bookingId);
    setPayError("");
    try {
      await api.post(`/bookings/${bookingId}/pay`);
      refreshBookings();
    } catch (e) {
      // 403 preview_blocked is intercepted globally — opens the signup gate.
      if (e?.response?.status !== 403) {
        setPayError("Payment didn't go through. Try again in a moment.");
      }
    } finally {
      setPayingId(null);
    }
  }, [payingId, refreshBookings]);

  useEffect(()=>{
    api.get("/bookings").then(r=>setBookings(Array.isArray(r.data) ? r.data : [])).catch(()=>setBookings([]));
    api.get("/ai/plans").then(r=>setPlans(Array.isArray(r.data) ? r.data : [])).catch(()=>setPlans([]));
    loadFavorites();
  },[loadFavorites]);

  const filteredBookings = useMemo(
    () => applyBookingFilters(bookings, { status: statusFilter, range: rangeFilter }),
    [bookings, statusFilter, rangeFilter]
  );
  const { statusCounts: bkStatusCounts, rangeCounts: bkRangeCounts } = useMemo(
    () => computeBookingFilterCounts(bookings),
    [bookings]
  );
  // "Next Experience" — soonest future Confirmed/Pending booking. Surfaced
  // as a featured hero so the most important info is the first thing the
  // customer sees on login. Falls back to null when no upcoming booking
  // exists; the rest of the dashboard renders unchanged.
  const nextExperience = useMemo(() => {
    const now = new Date();
    const future = (bookings || [])
      .filter((b) => {
        if (!b.date) return false;
        if (["Canceled", "Cancelled", "Completed"].includes(b.status)) return false;
        const dt = new Date(`${b.date}T${b.time || "00:00"}`);
        return !Number.isNaN(dt.getTime()) && dt >= now;
      })
      .sort((a, b) => {
        const ad = new Date(`${a.date}T${a.time || "00:00"}`).getTime();
        const bd = new Date(`${b.date}T${b.time || "00:00"}`).getTime();
        return ad - bd;
      });
    return future[0] || null;
  }, [bookings]);

  // Recent Plans — sort by updated_at|created_at DESC and lift any `active`
  // plans to the top. The API already returns plans sorted DESC by
  // created_at; we apply a tie-stable second pass here so:
  //   1. plans flagged "active" come first
  //   2. then most-recently-updated
  //   3. then most-recently-created
  const recentPlans = useMemo(() => {
    const score = (p) => {
      const updated = new Date(p.updated_at || p.created_at || 0).getTime();
      return (p.status === "active" ? 1e15 : 0) + updated;
    };
    return [...(plans || [])].sort((a, b) => score(b) - score(a));
  }, [plans]);
  const PLANS_DEFAULT_LIMIT = 2;
  const visiblePlans = plansExpanded
    ? recentPlans
    : recentPlans.slice(0, PLANS_DEFAULT_LIMIT);
  const hasMorePlans = recentPlans.length > PLANS_DEFAULT_LIMIT;

  const handlePlanDeleted = useCallback((planId) => {
    setPlans((prev) => prev.filter((p) => p.plan_id !== planId));
  }, []);
  const handlePlanUpdated = useCallback((updated) => {
    if (!updated?.plan_id) return;
    setPlans((prev) => prev.map((p) => (p.plan_id === updated.plan_id ? { ...p, ...updated } : p)));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 py-10 pb-28 lg:pb-10">
      <h1 className="font-serif text-4xl">Welcome back, {user?.name?.split(" ")[0]}</h1>
      <p className="text-[#071A2D]/70 mt-1">Let&apos;s create another unforgettable moment.</p>

      {/* 1. NEXT EXPERIENCE — soonest active booking surfaced first. Hidden when none. */}
      {nextExperience && (
        <div
          data-testid="next-experience-hero"
          className="mt-6 rounded-3xl overflow-hidden bg-[#071A2D] text-[#FFF8EE] shadow-luxury-lg border border-[#C9A45C]/30"
        >
          <div className="grid md:grid-cols-[1fr_auto] gap-6 p-6 items-center">
            <div className="flex items-center gap-5">
              {nextExperience.experience?.images?.[0] && (
                <img
                  src={nextExperience.experience.images[0]}
                  alt=""
                  className="w-20 h-20 rounded-2xl object-cover shrink-0"
                />
              )}
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9A45C] font-semibold">
                  Your next experience
                </div>
                <div className="font-serif text-2xl mt-1 truncate">{nextExperience.experience?.title || "Upcoming"}</div>
                <div className="text-xs opacity-75 mt-1.5">
                  {nextExperience.date} · {nextExperience.time}
                  {nextExperience.provider_user?.name ? ` · with ${nextExperience.provider_user.name}` : ""}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs px-2.5 py-1 rounded-full bg-[#C9A45C] text-[#071A2D] font-semibold">{nextExperience.status}</span>
              <Link
                to="/messages"
                data-testid="next-experience-message-cta"
                className="text-xs rounded-full bg-[#FFF8EE] text-[#071A2D] px-4 py-2 font-semibold hover:brightness-95"
              >
                Message Concierge →
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* 2. QUICK ACTIONS — reworked per the Apple/Airbnb mockup: four tight
          square tiles with a coral-tinted icon on a soft white surface. The
          AI Concierge entry ("New Experience") is the leftmost so it reads
          first; the other three (Saved Concierges, Bookings, Messages) align
          to the persistent mobile bottom-nav metaphor. */}
      <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <QuickActionTile
          to="/"
          testid="customer-cta-ai"
          icon={Sparkles}
          label="Start Request"
        />
        <QuickActionTile
          to="/customer#saved"
          testid="customer-cta-saved"
          icon={Heart}
          label="Saved Concierges"
        />
        <QuickActionTile
          to="/customer#bookings"
          testid="customer-cta-bookings"
          icon={Calendar}
          label="Bookings"
        />
        <QuickActionTile
          to="/messages"
          testid="customer-cta-messages"
          icon={MessageSquare}
          label="Messages"
        />
      </div>

      {/* 3. BOOKINGS LIST */}
      <h2 id="bookings" className="font-serif text-2xl mt-12 mb-3 scroll-mt-24">Your upcoming moments</h2>
      <FilterPills
        testid="customer-bookings-filters"
        status={statusFilter}
        range={rangeFilter}
        onStatusChange={setStatusFilter}
        onRangeChange={setRangeFilter}
        statusCounts={bkStatusCounts}
        rangeCounts={bkRangeCounts}
        className="mb-5"
      />
      <ExpandableBookingList
        items={filteredBookings}
        limit={3}
        testid="customer-upcoming-bookings"
        emptyState={
          <EmptyState
            testid="customer-empty-state"
            title="Your next unforgettable moment starts here."
            subtitle="Tell our AI Concierge what you have in mind — we'll match you with a TREL Concierge who handles every detail."
            cta={{ to: "/", label: "Tell TREL What You Need →", testid: "customer-empty-cta" }}
            tone="luxury"
          />
        }
        // Nearest upcoming date first. Bookings with no date fall to the end.
        // Same-date bookings tiebreak on time → newest created.
        sortBy={(a, b) => {
          const ad = a.date ? new Date(`${a.date}T${a.time || "00:00"}`).getTime() : Infinity;
          const bd = b.date ? new Date(`${b.date}T${b.time || "00:00"}`).getTime() : Infinity;
          if (ad !== bd) return ad - bd;
          return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
        }}
        renderItem={(b) => (
          <div key={b.booking_id} data-testid={`booking-${b.booking_id}`} className="p-5 rounded-2xl card-luxury flex items-center gap-5 flex-wrap">
            <img src={b.experience?.images?.[0]} alt="" className="w-20 h-20 rounded-xl object-cover bg-[#071A2D]/5"/>
            <div className="flex-1 min-w-[180px]">
              <div className="font-medium">{b.experience?.title}</div>
              <div className="text-sm text-[#071A2D]/60 mt-0.5">
                {(b.experience?.city || b.location) && (
                  <span>{b.experience?.city ? `${b.experience.city}${b.experience.state ? `, ${b.experience.state}` : ""}` : b.location} · </span>
                )}
                {b.date} · {b.time} · {b.guests} {b.guests === 1 ? "guest" : "guests"}
              </div>
              {b.provider_user?.name && (
                <div className="text-xs text-[#071A2D]/65 mt-1">
                  Concierge: <span className="font-semibold text-[#064E3B]">{b.provider_user.name}</span>
                </div>
              )}
              {b.provider_user?.name && (
                <div className="mt-2">
                  <TrustBadges provider={b.provider_user} size="sm"/>
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wide text-[#071A2D]/55 font-semibold">Concierge fee</div>
              <div className="font-semibold tabular-nums">${Number(b.amount || 0).toLocaleString()}</div>
              <div data-testid={`booking-stage-${b.booking_id}`} className="text-xs px-2 py-1 rounded-full bg-[#064E3B] text-[#FFF8EE] inline-block mt-1">{stageLabel(b)}</div>
            </div>

            {/* Inline "Pay concierge fee" CTA — surfaces directly on the
                dashboard whenever the customer still owes the service fee.
                Closes the loop between "Concierge Selected" and "Payment
                Completed" without forcing a navigation into the brief. */}
            {awaitingPayment(b) && (
              <div className="w-full sm:w-auto flex flex-col items-stretch sm:items-end gap-1">
                <button
                  type="button"
                  onClick={() => payConciergeFee(b.booking_id)}
                  disabled={payingId === b.booking_id}
                  data-testid={`pay-concierge-fee-${b.booking_id}`}
                  aria-label={`Pay concierge fee of $${Number(b.amount || 0).toLocaleString()}`}
                  className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#064E3B] text-white hover:brightness-110 disabled:opacity-70 disabled:cursor-not-allowed px-4 py-2 text-[12.5px] font-semibold shadow-[0_10px_22px_-12px_rgba(6,78,59,0.7)] transition"
                >
                  {payingId === b.booking_id ? (
                    <><Loader2 size={13} className="animate-spin"/> Paying…</>
                  ) : (
                    <><CreditCard size={13}/> Pay concierge fee · ${Number(b.amount || 0).toLocaleString()}</>
                  )}
                </button>
                {payError && payingId === null && (
                  <span data-testid={`pay-error-${b.booking_id}`} role="alert" className="text-[11px] text-[#B45309]">{payError}</span>
                )}
              </div>
            )}
            {!["Completed", "Canceled", "Cancelled"].includes(b.status) && (
              <button
                onClick={() => setShareBooking({ ...b, _mode: "safety" })}
                data-testid={`share-trip-${b.booking_id}`}
                className="rounded-full bg-[#FF7A45] text-[#FFF8EE] px-3.5 py-2 text-xs font-semibold inline-flex items-center gap-1.5 hover:brightness-110"
              >
                <Share2 size={12}/> Share trip
              </button>
            )}
            {b.status === "Completed" && (
              <button
                onClick={() => setShareBooking({ ...b, _mode: "referral" })}
                data-testid={`share-concierge-${b.booking_id}`}
                className="rounded-full bg-[#064E3B] text-[#FFF8EE] px-3.5 py-2 text-xs font-semibold inline-flex items-center gap-1.5 hover:brightness-110"
              >
                <Share2 size={12}/> Share My Concierge
              </button>
            )}
          </div>
        )}
      />

      {/* MY CONCIERGE REQUESTS — Phase 46 brief marketplace inbox */}
      <MyConciergeRequests />

      {/* CUSTOM OFFERS — concierge proposals waiting on customer Accept/Decline */}
      <CustomOffersPanel onChange={() => api.get("/bookings").then(r=>setBookings(Array.isArray(r.data) ? r.data : [])).catch(()=>{})}/>

      {/* SAVED CONCIERGES — heart-saved experts from across the marketplace */}
      <div id="saved" className="mt-12 scroll-mt-24">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-serif text-2xl flex items-center gap-2">
            <Heart size={18} className="text-[#FF7A45]" fill="#FF7A45"/>
            Saved Concierges
          </h2>
          {favorites.length > 0 && (
            <span className="text-xs text-[#071A2D]/55">{favorites.length} saved</span>
          )}
        </div>
        {favorites.length === 0 ? (
          <EmptyState
            testid="customer-favorites-empty"
            icon={Heart}
            title="Save your favorite Concierges."
            subtitle="Tap the heart on any Concierge card to bookmark them. They'll show up here so you can start planning again in one click."
            cta={{ to: "/explore", label: "Browse Concierges →", testid: "customer-favorites-empty-cta" }}
          />
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="customer-saved-concierges">
            {favorites.map((f) => (
              <SavedConciergeTile
                key={f.favorite_id}
                fav={f}
                onUnsaved={loadFavorites}
              />
            ))}
          </div>
        )}
      </div>

      {/* RECENT PLANS — top 3 by default, expand on demand. Cleaner first view. */}
      <div className="mt-12" data-testid="customer-recent-plans">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-serif text-2xl flex items-center gap-2">
            <Sparkles size={18} className="text-[#C9A45C]"/>
            Recent Plans
          </h2>
          {recentPlans.length > 0 && (
            <span className="text-xs text-[#071A2D]/55">{recentPlans.length} total</span>
          )}
        </div>
        {recentPlans.length === 0 ? (
          <EmptyState
            testid="customer-plans-empty"
            icon={Sparkles}
            title="Your unforgettable moments will appear here."
            subtitle="Tell our AI Concierge what you have in mind — every saved plan, draft, and idea lives here."
            cta={{ to: "/", label: "Tell TREL What You Need →", testid: "customer-plans-empty-cta" }}
          />
        ) : (
          <>
            {/* Compact rich card per Phase 43-B: View Details / Edit / Delete
                with confirmation. Default 2 cards; "View More" reveals the
                rest so the dashboard stays calm. */}
            <div className="grid sm:grid-cols-2 gap-3">
              {visiblePlans.map((p) => (
                <RecentPlanCard
                  key={p.plan_id}
                  plan={p}
                  onDeleted={handlePlanDeleted}
                  onUpdated={handlePlanUpdated}
                  readOnly={!!user?.preview_account}
                />
              ))}
            </div>
            {hasMorePlans && (
              <div className="mt-5 flex justify-center">
                <button
                  type="button"
                  data-testid="recent-plans-toggle"
                  onClick={() => setPlansExpanded((v) => !v)}
                  className="text-sm font-semibold text-[#FF7A45] inline-flex items-center gap-1.5 hover:translate-y-px transition"
                >
                  {plansExpanded ? (
                    <>Show Less <ChevronUp size={14}/></>
                  ) : (
                    <>View More <ChevronDown size={14}/></>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <ShareMyExperience
        open={!!shareBooking}
        booking={shareBooking}
        onClose={() => setShareBooking(null)}
      />
    </div>
  );
}

// Quick-action tile — premium pure-white surface with a coral-tinted icon
// and a single short label. Modelled on the mockup's 2x2 / 4-up tile row.
// Stays clickable + obvious (soft shadow, ring on hover) without competing
// with the Next Experience hero above or the bookings list below.
function QuickActionTile({ to, testid, icon: Icon, label }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group rounded-2xl bg-white border border-[#071A2D]/8 shadow-[0_4px_16px_-8px_rgba(7,26,45,0.18)] hover:shadow-[0_10px_28px_-12px_rgba(7,26,45,0.28)] hover:-translate-y-0.5 transition-all duration-200 px-3 sm:px-4 py-5 sm:py-6 flex flex-col items-center justify-center text-center"
    >
      <span className="w-11 h-11 rounded-full bg-[#FF7A45]/10 text-[#FF7A45] flex items-center justify-center group-hover:bg-[#FF7A45]/15 transition-colors">
        <Icon size={20} strokeWidth={2.2}/>
      </span>
      <span className="mt-3 text-[12px] sm:text-[13px] font-semibold text-[#071A2D] leading-tight">
        {label}
      </span>
    </Link>
  );
}

// Saved-Concierge tile — compact, links to the public profile, unsave inline.
function SavedConciergeTile({ fav, onUnsaved }) {
  const c = fav.concierge || {};
  const href = `/concierge/${c.slug || ""}`;
  return (
    <Link
      to={href}
      data-testid={`saved-concierge-${c.user_id}`}
      className="group relative rounded-3xl overflow-hidden card-luxury hover:-translate-y-0.5 transition flex"
    >
      <img
        src={c.photo}
        alt={c.name}
        className="w-24 h-24 object-cover shrink-0 group-hover:scale-105 transition-transform duration-500"
      />
      <div className="flex-1 p-4 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-serif text-base text-[#071A2D] truncate">{c.name}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#064E3B] font-semibold mt-1 truncate">
              {(c.categories || [])[0] || "Concierge"}
            </div>
            {c.city && (
              <div className="text-[11px] text-[#071A2D]/60 mt-1 truncate">{c.city}, {c.state}</div>
            )}
          </div>
          <FavoriteButton
            conciergeUserId={c.user_id}
            isDemo={c.is_demo_profile}
            initialSaved
            size="sm"
            onChange={(saved) => { if (!saved) onUnsaved?.(); }}
            testid={`saved-tile-heart-${c.user_id}`}
          />
        </div>
        <span className="mt-2 inline-block text-[11px] font-semibold text-[#FF7A45] group-hover:translate-x-0.5 transition">
          Start Planning →
        </span>
      </div>
    </Link>
  );
}
